import { useCrudService } from 'framework/crud';
import { ListResponse } from 'framework/list';
import { usePeopleManagementApi } from 'infrastructure/api';
import { useTechnologyService } from 'domain/shared/career';
import { useBusinessFunctionService } from 'domain/shared/businessFunction';
import { useOfficeService, usePositionService } from 'domain/shared/employee';
import usePositionSlotsService from 'domain/people/positionSlots/usePositionSlotsService';
import { PositionSlot, PositionSlotListItem } from 'domain/people/positionSlots/types';
import { enrichPositionSlots } from 'domain/people/positionSlots/utils/positionSlotDisplay';
import {
  isPositionSlotLoadHttpError,
  mapPositionSlotToFormData,
  EmployeePositionSlotFormData,
  POSITION_SLOT_LOAD_STATUS,
} from './positionSlotFormData';

const RESOURCE_URL = '/position-slots';

const useEmployeePositionSlotService = () => {
  const { assignEmployee, unassignEmployee } = usePositionSlotsService();
  const { findAll: findAllTechnologies } = useTechnologyService();
  const { findAll: findAllPositions } = usePositionService();
  const { findAll: findAllOffices } = useOfficeService();
  const { findAll: findAllBusinessFunctions } = useBusinessFunctionService();
  const { search } = useCrudService<PositionSlot, ListResponse<PositionSlot>>({
    listResourceUrl: RESOURCE_URL,
    singleResourceUrl: `${RESOURCE_URL}/:id`,
    useService: usePeopleManagementApi,
  });

  const enrichPositionSlotList = async (positionSlots: PositionSlot[]): Promise<PositionSlotListItem[]> => {
    const [positions, technologies, offices, businessFunctions] = await Promise.all([
      findAllPositions(),
      findAllTechnologies(),
      findAllOffices(),
      findAllBusinessFunctions(),
    ]);

    return enrichPositionSlots(positionSlots, {
      positions,
      technologies,
      offices,
      businessFunctions,
    });
  };

  const getEmployeePositionSlotAssignValidationSchema = () =>
    Promise.resolve({
      assignPositionSlotId: {
        type: 'string',
        required: true,
      },
    });

  const searchEmployeePositionSlot = (employeeAlias: string): Promise<ListResponse<PositionSlot>> =>
    search({
      filter: {
        employeeAlias: {
          eq: employeeAlias,
        },
      },
      pageSize: 1,
    });

  const getEmployeePositionSlotFormData = async (employeeAlias: string): Promise<EmployeePositionSlotFormData> => {
    try {
      const response = await searchEmployeePositionSlot(employeeAlias);

      const positionSlot = response.result?.[0];
      if (!positionSlot) {
        return mapPositionSlotToFormData(null, employeeAlias, {
          loadStatus: POSITION_SLOT_LOAD_STATUS.NOT_FOUND,
        });
      }

      const [enrichedPositionSlot] = await enrichPositionSlotList([positionSlot]);

      return mapPositionSlotToFormData(enrichedPositionSlot, employeeAlias, {
        loadStatus: POSITION_SLOT_LOAD_STATUS.SUCCESS,
      });
    } catch (error) {
      if (isPositionSlotLoadHttpError(error)) {
        return mapPositionSlotToFormData(null, employeeAlias, {
          loadStatus: POSITION_SLOT_LOAD_STATUS.ERROR,
        });
      }

      throw error;
    }
  };

  const getEmployeePositionSlotId = async (employeeAlias: string): Promise<string | null> => {
    try {
      const response = await searchEmployeePositionSlot(employeeAlias);

      return response.result?.[0]?.id ?? null;
    } catch (error) {
      if (isPositionSlotLoadHttpError(error)) {
        return null;
      }

      throw error;
    }
  };

  const assignEmployeeToPositionSlot = async (
    employeeAlias: string,
    positionSlotId: string,
    currentPositionSlotId: string | null,
  ) => {
    if (positionSlotId !== currentPositionSlotId) {
      if (currentPositionSlotId) {
        await unassignEmployee(currentPositionSlotId, employeeAlias);
      }

      await assignEmployee(positionSlotId, { employeeAlias });
    }

    const formData = await getEmployeePositionSlotFormData(employeeAlias);

    return {
      ...formData,
      currentPositionSlotId: formData.positionSlotId,
      assignPositionSlotId: null,
    };
  };

  return {
    getEmployeePositionSlotFormData,
    getEmployeePositionSlotId,
    assignEmployeeToPositionSlot,
    getEmployeePositionSlotAssignValidationSchema,
  };
};

export default useEmployeePositionSlotService;
